/**
 * Browser-kept copies of data that almost never changes — the PLSS section,
 * county and state shapes behind the deals map — so a map's first render on a
 * later visit skips the fetch altogether. IndexedDB, not localStorage: a
 * county's shapes run to a megabyte or two, past localStorage's few-MB quota.
 *
 * Entries expire after the `ttlMs` they were written with; an expired entry
 * reads as missing and is deleted.
 *
 * Never throws, like utils/uiPrefs. IndexedDB is missing in some private
 * windows, can be blocked or over quota, and is absent in the test DOM; every
 * failure reads as "not stored" and the caller just fetches.
 *
 * Callers put the signed-in user's oid in every key.
 */

// Bump VERSION when the stored value's shape changes; old entries are then
// never read again.
const DB_NAME = "bd.persistentCache";
const VERSION = 1;
const STORE = "entries";

let dbPromise = null;

const openDb = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      const idb = globalThis.indexedDB;
      if (!idb) return resolve(null);
      const req = idb.open(DB_NAME, VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (db.objectStoreNames.contains(STORE)) db.deleteObjectStore(STORE);
        db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
};

/** Runs `fn(store)` in one transaction; resolves the request's result, or null. */
const withStore = async (mode, fn) => {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const req = fn(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
};

/**
 * @param {string} key
 * @returns {Promise<unknown|null>} the stored value, or null if missing,
 *   expired or storage is unusable
 */
export const readPersisted = async (key) => {
  const entry = await withStore("readonly", (s) => s.get(key));
  if (!entry || typeof entry.expiresAt !== "number") return null;
  if (Date.now() >= entry.expiresAt) {
    await withStore("readwrite", (s) => s.delete(key));
    return null;
  }
  return entry.value;
};

/**
 * @param {string} key
 * @param {unknown} value  anything structured-cloneable
 * @param {number} ttlMs
 * @returns {Promise<void>}
 */
export const writePersisted = async (key, value, ttlMs) => {
  await withStore("readwrite", (s) => s.put({ value, expiresAt: Date.now() + ttlMs }, key));
};

/** Test hook: forget the open connection so a new fake IndexedDB is used. */
export const resetPersistentCacheForTests = () => {
  dbPromise = null;
};
